/**
 * Forløb context for an appointment row.
 *
 * Journal already resolves this from the patient's booked courses: a step
 * points at an appointment, and "Trin N af M" is that step's 1-based place
 * after sorting by sort_order. Patient → Aftaler uses the same rule.
 */

export type AppointmentCourseSource = {
  id: string;
  template_name: string;
  steps: readonly {
    id: string;
    sort_order: number;
    appointment_id?: string | null;
    title?: string | null;
  }[];
};

export type AppointmentCourseContext = {
  courseId: string;
  courseName: string;
  stepId: string;
  /** 1-based place after sorting by sort_order. Matches the journal meta line. */
  stepIndex: number;
  stepCount: number;
  stepTitle: string | null;
};

export function courseContextByAppointmentId(
  courses: readonly AppointmentCourseSource[],
): Map<string, AppointmentCourseContext> {
  const map = new Map<string, AppointmentCourseContext>();
  for (const course of courses) {
    const ordered = [...course.steps].sort((a, b) => a.sort_order - b.sort_order);
    ordered.forEach((step, index) => {
      if (!step.appointment_id) return;
      const title = step.title?.trim() || null;
      map.set(step.appointment_id, {
        courseId: course.id,
        courseName: course.template_name,
        stepId: step.id,
        stepIndex: index + 1,
        stepCount: ordered.length,
        stepTitle: title,
      });
    });
  }
  return map;
}

function normalizeLabel(value: string | null | undefined): string {
  return (value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
}

/**
 * Step titles that only repeat the row title, the course name, or "Trin N"
 * stay off the line. The appointment row already shows the service name.
 */
function stepTitleForLine(
  context: AppointmentCourseContext,
  visibleTitle: string | null | undefined,
): string | null {
  const title = context.stepTitle?.trim() ?? '';
  if (!title) return null;
  const normalized = normalizeLabel(title);
  if (normalized === normalizeLabel(context.courseName)) return null;
  if (normalized === normalizeLabel(visibleTitle)) return null;
  const generic = new Set([
    `trin ${context.stepIndex}`,
    `trin ${context.stepIndex} af ${context.stepCount}`,
    `step ${context.stepIndex}`,
    `step ${context.stepIndex} of ${context.stepCount}`,
  ]);
  if (generic.has(normalized)) return null;
  return title;
}

/**
 * Quiet secondary line: course name, a distinct step title, then Trin N af M.
 * `stepOfTotal` is the already translated badge ("Trin 1 af 5").
 * Returns null when there is nothing to say, so standalone appointments
 * keep their current row.
 */
export function formatAppointmentCourseContextLine(
  context: AppointmentCourseContext,
  options: { visibleTitle?: string | null; stepOfTotal: string },
): string | null {
  const courseName = context.courseName.trim();
  const stepOfTotal = options.stepOfTotal.trim();
  const stepTitle = stepTitleForLine(context, options.visibleTitle);
  const parts = [courseName, stepTitle, stepOfTotal].filter((part): part is string =>
    Boolean(part),
  );
  return parts.length > 0 ? parts.join(' · ') : null;
}
